import { useQuery } from '@tanstack/react-query';
import { getApiClient } from '../api/apiClient';

const apiClient = getApiClient('kisanVani');

// `key` identifies the card in the UI; `status` is the value the API understands.
// Posts are never stored as 'approved' (the DB only allows draft / published / archived /
// pending / rejected) - approving a post sets it to 'published', so the Approved card
// queries 'published'.
export const POST_STATUS_FILTERS = [
  { key: 'all', label: 'Total', status: 'all' },
  { key: 'approved', label: 'Approved', status: 'published' },
  { key: 'pending', label: 'Pending', status: 'pending' },
  { key: 'rejected', label: 'Rejected', status: 'rejected' }
];

export const getApiStatus = (key) =>
  POST_STATUS_FILTERS.find((f) => f.key === key)?.status ?? 'all';

const UNIQUE_API_STATUSES = [...new Set(POST_STATUS_FILTERS.map((f) => f.status))];

// Fetches a single row per status and reads the server-side totalCount, so the
// counts stay accurate without downloading every post.
const fetchStatusCount = async (status, dateFilter) => {
  let url = `/posts/all?status=${status}&page=1&limit=1`;
  if (dateFilter?.fromDate) url += `&fromDate=${encodeURIComponent(dateFilter.fromDate)}`;
  if (dateFilter?.toDate) url += `&toDate=${encodeURIComponent(dateFilter.toDate)}`;

  const response = await apiClient.get(url);
  if (!response.data?.success) {
    throw new Error(`Failed to load ${status} post count`);
  }
  return Number(response.data.data?.totalCount ?? 0);
};

// The query key starts with 'posts', so invalidating ['posts'] (done by verifyPost)
// refreshes these counts whenever a post's status changes.
export const usePostStatusCounts = (dateFilter = null) =>
  useQuery({
    queryKey: ['posts', 'statusCounts', dateFilter?.fromDate ?? null, dateFilter?.toDate ?? null],
    queryFn: async () => {
      const results = await Promise.all(
        UNIQUE_API_STATUSES.map((status) => fetchStatusCount(status, dateFilter))
      );
      const byStatus = Object.fromEntries(UNIQUE_API_STATUSES.map((s, i) => [s, results[i]]));
      return Object.fromEntries(POST_STATUS_FILTERS.map(({ key, status }) => [key, byStatus[status]]));
    },
    staleTime: 60 * 1000
  });
